import { makeAutoObservable, runInAction } from 'mobx';
import { isValidEmail, roundToPrecision } from '../../utils/utils';
import { BUSINESS_DAYS_PER_YEAR, INVALID_EMAIL_PROVIDERS } from '../../App.constants';
import { IHubspotFormConfig, sendFormData } from '../../utils/form.utils';

export class RevenueCalculatorStore {
  public isStoreReady: boolean = false;
  public startingAnnualRevenue: number = 10000000;
  public averageHourlyBillRate: number = 150;
  public billableEmployeeCount: number = 50;
  public billableEmployeeUtilizationTarget: number = 85;
  public nonBillEmployeeCount: number = 5;
  public averageWorkingHoursPerWeek: number = 40;
  public averagePtoDaysPerYear: number = 13;

  public increaseBillableUtilization: number = 2;
  public increaseAverageBillRate: number = 5;
  public increaseBillableHeadcount: number = 5;

  public hasSubmittedEmail: boolean = false;
  public emailAddress: string = '';

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  get totalBusinessDays(): number {
    return BUSINESS_DAYS_PER_YEAR - this.averagePtoDaysPerYear;
  }

  get hoursPerDay(): number {
    return roundToPrecision(this.averageWorkingHoursPerWeek / 5, 2);
  }

  get hoursPerYear(): number {
    return this.hoursPerDay * this.totalBusinessDays;
  }

  get availableBillableHours(): number {
    return roundToPrecision(this.billableEmployeeCount * (this.totalBusinessDays * this.hoursPerDay), 2);
  }

  get billableHours(): number {
    return roundToPrecision(this.availableBillableHours * (this.billableEmployeeUtilizationTarget / 100), 2);
  }

  get availableNonBillHours(): number {
    return roundToPrecision(this.nonBillEmployeeCount * (this.totalBusinessDays * this.hoursPerDay), 2);
  }

  get currentRevenuePotential(): number {
    return roundToPrecision(this.billableHours * this.averageHourlyBillRate, 2);
  }

  get unCapturedRevenue(): number {
    return this.currentRevenuePotential - this.startingAnnualRevenue;
  }

  get revenuePerBillableEmployee(): number {
    return roundToPrecision(this.currentRevenuePotential / this.billableEmployeeCount, 2);
  }

  get revenuePerEmployee(): number {
    return roundToPrecision(this.currentRevenuePotential / (this.billableEmployeeCount + this.nonBillEmployeeCount), 2);
  }

  get targetAverageUtilization(): number {
    const totalAvailable = this.availableBillableHours + this.availableNonBillHours;
    const totalBillableHours = this.billableHours;

    return roundToPrecision(totalBillableHours / totalAvailable, 2) * 100;
  }

  get potentialRevenueCapacityWithUpdates(): number {
    const updatedBillableUtilization =
      (this.billableEmployeeUtilizationTarget + this.increaseBillableUtilization) / 100;
    const updatedBillRate = this.averageHourlyBillRate + this.increaseAverageBillRate;
    const updatedHeadcount = this.billableEmployeeCount + this.increaseBillableHeadcount;
    const billableHoursUpdated = roundToPrecision(updatedHeadcount * (this.totalBusinessDays * this.hoursPerDay), 2);

    return roundToPrecision(billableHoursUpdated * updatedBillRate * updatedBillableUtilization, 2);
  }

  get increaseDecreaseAmount(): number {
    return this.potentialRevenueCapacityWithUpdates - this.currentRevenuePotential;
  }

  get potentialRevenueCapacityWithUpdatesPercent(): number {
    const difference = this.potentialRevenueCapacityWithUpdates - this.currentRevenuePotential;

    return roundToPrecision((difference / this.potentialRevenueCapacityWithUpdates) * 100, 2);
  }

  get isValidWorkEmail(): boolean {
    if (!isValidEmail(this.emailAddress)) {
      return false;
    }

    const provider = this.emailAddress.split('@')[1];

    return !INVALID_EMAIL_PROVIDERS.includes(provider);
  }

  get isValidEmail(): boolean {
    return isValidEmail(this.emailAddress);
  }

  updateStartingAnnualRevenue(newValue: number): void {
    this.startingAnnualRevenue = newValue;
  }

  updateAverageHourlyBillRate(newValue: number): void {
    this.averageHourlyBillRate = newValue;
  }

  updateEmployeeCount(newValue: number): void {
    this.billableEmployeeCount = newValue;
  }

  updateEmployeeBillableTarget(newValue: number): void {
    this.billableEmployeeUtilizationTarget = newValue;
  }

  updateNonBillEmployeeCount(newValue: number): void {
    this.nonBillEmployeeCount = newValue;
  }

  updateWorkingHoursPerWeek(newValue: number): void {
    this.averageWorkingHoursPerWeek = newValue;
  }

  updateAveragePtoDays(newValue: number): void {
    this.averagePtoDaysPerYear = newValue;
  }

  updateIncreaseBillableUtilization(newValue: number): void {
    this.increaseBillableUtilization = newValue;
  }

  updateIncreaseAverageBillRate(newValue: number): void {
    this.increaseAverageBillRate = newValue;
  }

  updateIncreaseBillableHeadcount(newValue: number): void {
    this.increaseBillableHeadcount = newValue;
  }

  updateEmailAddress(emailValue: string): void {
    this.emailAddress = emailValue;
  }

  async submitEmail(): Promise<void> {
    if (this.isValidWorkEmail) {
      const formData: IHubspotFormConfig = {
        email: this.emailAddress,
        average_hourly_bill_rate: this.averageHourlyBillRate.toString(),
        revcap_annual_revenue: this.startingAnnualRevenue.toString(),
        revcap_hours_week: this.averageWorkingHoursPerWeek.toString(),
        revcap_pto_year: this.averagePtoDaysPerYear.toString(),
        revcap_nonbill_headcount: this.nonBillEmployeeCount.toString(),
        revcap_potential: this.currentRevenuePotential.toString(),
        target_billable_utilization__assuming_a_40_hours__100: this.billableEmployeeUtilizationTarget.toString(),
        total_billable_headcount: this.billableEmployeeCount.toString(),
      };

      await sendFormData(formData);

      this.hasSubmittedEmail = true;
    }
  }
  /**
   * Store initializer. Should only be called once.
   */
  async init(): Promise<void> {
    runInAction(() => {
      this.isStoreReady = true;
    });
  }
}
